/**
 * LoginPage.js
 *
 * Email + password sign-in form with split-screen layout.
 *
 * Validation rules (intentionally different from SignupPage):
 *   - Login only checks that email and password are non-empty.
 *   - It does NOT perform password-confirmation or length checks —
 *     those belong exclusively to signup. Any password mismatch here
 *     is an auth-level error from Supabase ("incorrect credentials"),
 *     not a form validation error.
 *
 * Includes detection for unverified-email errors and offers a
 * "Resend confirmation" action inline.
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, CheckCircle2 } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';
import BrandLogo from '@/components/BrandLogo';
import LegalFooter from '@/components/LegalFooter';
import AuthBrandPanel from '@/components/AuthBrandPanel';

export default function LoginPage() {
  const { signIn, resendConfirmation } = useAuth();
  const [searchParams] = useSearchParams();
  // RC 1.44.O1.B — `?just_signed_up=1` arrives when the caller was
  // redirected here by <PlanActivationHandoff> after a successful
  // Base or Paid plan activation. We render a single-line welcome
  // banner above the form. No auth-logic changes — the user still
  // completes a normal sign-in below.
  const justSignedUp = searchParams.get('just_signed_up') === '1';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendStatus, setResendStatus] = useState('');

  /**
   * Handle login form submission.
   * Only validates presence of email + password — no length or match checks.
   * All credential-level errors come from Supabase via the signIn() call.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setShowResend(false); setResendStatus('');

    if (!email.trim() || !password) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    const { error: authError } = await signIn(email.trim(), password);
    if (authError) {
      setError(authError.message);
      setShowResend(!!authError._isEmailUnverified);
      setLoading(false);
    }
    // On success: onAuthStateChange fires → PublicRoute redirects automatically
  };

  const handleResend = async () => {
    setResendStatus('sending');
    const { error: err } = await resendConfirmation(email.trim());
    setResendStatus(err ? 'error' : 'sent');
  };

  return (
    <div className="flex flex-col min-h-screen bg-ndor-canvas" data-testid="login-page">
      <div className="flex flex-1">
      {/* Left — Premium brand hero (desktop only) */}
      <div className="hidden lg:flex lg:flex-1 relative brand-hex-grid">
        <AuthBrandPanel variant="desktop" />
      </div>

      {/* Right — Auth form */}
      <div className="flex flex-1 min-w-0 flex-col justify-center px-6 py-10 sm:py-12 lg:px-16 bg-ndor-surface">
        <div className="mx-auto w-full max-w-md sm:max-w-lg lg:max-w-sm">
          {/* RC 1.35.F3 follow-up — full mobile/tablet brand hero
              (logo + headline + value cards + tagline) replaces the
              previous logo-only mobile block. Hidden on lg+ where the
              left hero takes over. */}
          <AuthBrandPanel variant="mobile" />
          {/* Desktop compact brand for the right column (kept for visual anchor) */}
          <div className="hidden lg:flex mb-12 items-center gap-3">
            <BrandLogo variant="symbol" size={44} />
          </div>

          <h1 className="text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink">Welcome back</h1>
          <p className="text-sm sm:text-[15px] text-ndor-ink-secondary mt-2">Sign in to your account to continue.</p>

          {justSignedUp && (
            <div
              className="mt-5 flex items-start gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200"
              data-testid="login-just-signed-up-banner"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-[2px]" />
              <span>Welcome back — sign in to activate your plan.</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
            {error && (
              <div className="rounded-lg bg-ndor-error-light border border-ndor-error-border p-3 text-sm text-ndor-error" data-testid="login-error">
                {error}
                {showResend && (
                  <div className="mt-2 pt-2 border-t border-ndor-error-border">
                    {resendStatus === 'sent' ? (
                      <p className="text-ndor-success text-xs flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Confirmation email resent.</p>
                    ) : (
                      <button type="button" onClick={handleResend} disabled={resendStatus === 'sending'} className="text-xs font-medium underline hover:no-underline disabled:opacity-50" data-testid="login-resend-confirmation">
                        {resendStatus === 'sending' ? 'Sending...' : 'Resend confirmation email'}
                      </button>
                    )}
                    {resendStatus === 'error' && <p className="text-xs mt-1">Could not resend. Try again shortly.</p>}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-ndor-ink">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-ndor-ink">Password</Label>
              <PasswordInput
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-medium"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
            <div className="text-right">
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password-link"
                className="text-xs text-ndor-ink-muted hover:text-ndor-ink hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </form>

          <p className="mt-8 text-center text-sm text-ndor-ink-secondary font-manrope">
            Don't have an account?{' '}
            <Link to="/signup" className="font-medium text-ndor-ink hover:underline" data-testid="goto-signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
      </div>
      <LegalFooter variant="auth" />
    </div>
  );
}
